<template>
  <v-menu
    :value="value"
    @input="onMenuInput"
    bottom
    left
    offset-y
    :close-on-content-click="false"
    max-width="400"
    min-width="300"
    :nudge-bottom="6"
  >
    <template v-slot:activator="{ on, attrs }">
      <v-btn
        class="facility-activator"
        text
        depressed
        :ripple="false"
        height="20"
        v-bind="attrs"
        v-on="on"
      >
        <v-icon small color="bg_force_white" class="activator-lead-icon">mdi-office-building-outline</v-icon>
        <span class="text-truncate activator-label">{{ activatorLabel }}</span>
        <v-icon right small color="bg_force_white">mdi-chevron-down</v-icon>
      </v-btn>
    </template>

    <v-card class="facility-dropdown-card">
      <v-card-title class="py-2 px-4 d-flex align-center">
        <span class="text-h6">Facilities</span>
        <v-spacer></v-spacer>
        <span v-if="!loading && totalCount" class="text-caption text--secondary">
          {{ totalCount }}
        </span>
      </v-card-title>

      <v-card-text class="pb-0 px-4">
        <v-text-field
          v-model="searchInput"
          placeholder="Search facilities..."
          prepend-inner-icon="mdi-magnify"
          outlined
          dense
          clearable
          hide-details
          autofocus
          ref="searchField"
        ></v-text-field>
      </v-card-text>

      <!-- Loading state (first fetch) -->
      <div
        v-if="loading && !facilities.length"
        class="d-flex align-center justify-center py-6"
      >
        <v-progress-circular indeterminate color="primary" size="28" width="2" />
      </div>

      <!-- Error state (only when we have nothing to show) -->
      <div
        v-else-if="error && !facilities.length"
        class="px-4 py-4 text-center"
      >
        <v-icon color="error" class="mb-2">mdi-alert-circle-outline</v-icon>
        <div class="text-body-2 error--text mb-2">{{ error }}</div>
        <v-btn x-small color="primary" outlined @click="$emit('retry')">
          Retry
        </v-btn>
      </div>

      <v-list
        v-else
        dense
        max-height="320"
        class="compact-list py-0"
        style="overflow-y: auto;"
      >
        <!-- Favorites section -->
        <template v-if="filteredFavorites.length">
          <v-subheader class="favorites-section-header compact-subheader">
            <v-icon x-small color="amber" class="mr-1">mdi-star</v-icon>
            Favorites
          </v-subheader>
          <v-list-item
            v-for="facility in filteredFavorites"
            :key="'fav-' + facility.id"
            :href="hrefFor(facility)"
            @click="handleItemClick(facility, $event)"
            @auxclick="handleAuxClick(facility, $event)"
            class="compact-list-item"
            :class="{ 'facility-item--current': facility.id === currentFacilityId }"
          >
            <v-list-item-avatar size="24" class="my-0 mr-2">
              <v-icon
                small
                :color="facility.id === currentFacilityId ? 'success' : 'amber darken-2'"
              >
                {{ facility.id === currentFacilityId ? 'mdi-check-circle' : 'mdi-star' }}
              </v-icon>
            </v-list-item-avatar>
            <v-list-item-content class="py-1">
              <v-list-item-title class="d-flex align-center text-body-2">
                <span class="text-truncate mr-1 font-weight-medium">{{ facility.name }}</span>
                <v-chip
                  v-if="facility.id === currentFacilityId"
                  x-small
                  color="success"
                  label
                  class="compact-chip"
                >
                  Current
                </v-chip>
              </v-list-item-title>
            </v-list-item-content>
          </v-list-item>
          <v-divider
            v-if="filteredOthers.length"
            class="my-1 facility-separator"
          ></v-divider>
        </template>

        <!-- All (non-favorite) facilities section -->
        <template v-if="filteredOthers.length">
          <v-subheader
            v-if="filteredFavorites.length"
            class="all-section-header compact-subheader"
          >
            All Facilities
          </v-subheader>
          <v-list-item
            v-for="facility in filteredOthers"
            :key="facility.id"
            :href="hrefFor(facility)"
            @click="handleItemClick(facility, $event)"
            @auxclick="handleAuxClick(facility, $event)"
            class="compact-list-item"
            :class="{ 'facility-item--current': facility.id === currentFacilityId }"
          >
            <v-list-item-avatar size="24" class="my-0 mr-2">
              <v-icon
                small
                :color="facility.id === currentFacilityId ? 'success' : 'grey'"
              >
                {{ facility.id === currentFacilityId ? 'mdi-check-circle' : 'mdi-office-building-outline' }}
              </v-icon>
            </v-list-item-avatar>
            <v-list-item-content class="py-1">
              <v-list-item-title class="d-flex align-center text-body-2">
                <span class="text-truncate mr-1">{{ facility.name }}</span>
                <v-chip
                  v-if="facility.id === currentFacilityId"
                  x-small
                  color="success"
                  label
                  class="compact-chip"
                >
                  Current
                </v-chip>
              </v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </template>

        <!-- Empty states -->
        <v-list-item
          v-if="!loading && !error && !filteredFavorites.length && !filteredOthers.length"
          disabled
          class="compact-list-item"
        >
          <v-list-item-content class="py-2">
            <v-list-item-title class="text-center text--secondary text-body-2">
              <template v-if="search">No facilities match "{{ search }}"</template>
              <template v-else>No other facilities available</template>
            </v-list-item-title>
          </v-list-item-content>
        </v-list-item>
      </v-list>
    </v-card>
  </v-menu>
</template>

<script>
import { guardDashboardExit } from '@Dashboard/composables/dashboardExitGuard';

export default {
  name: 'FacilityDropdown',
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    facilities: {
      type: Array,
      default: () => [],
    },
    currentFacilityId: {
      type: String,
      default: null,
    },
    currentFacilityName: {
      type: String,
      default: '',
    },
    loading: {
      type: Boolean,
      default: false,
    },
    error: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      searchInput: '',
      search: '',
    };
  },
  computed: {
    activatorLabel() {
      return this.currentFacilityName || 'Facility';
    },
    totalCount() {
      return this.facilities.length;
    },
    sortedFacilities() {
      // Filter before any allocation (per CLAUDE.md perf guidance).
      const term = (this.search || '').trim().toLowerCase();
      const matches = term
        ? this.facilities.filter(
            (f) => f && f.name && f.name.toLowerCase().includes(term)
          )
        : this.facilities.slice();

      matches.sort((a, b) => {
        const af = a.isFavorite ? 1 : 0;
        const bf = b.isFavorite ? 1 : 0;
        if (af !== bf) return bf - af;
        return (a.name || '').localeCompare(b.name || '');
      });
      return matches;
    },
    filteredFavorites() {
      return this.sortedFacilities.filter((f) => f.isFavorite);
    },
    filteredOthers() {
      return this.sortedFacilities.filter((f) => !f.isFavorite);
    },
  },
  watch: {
    searchInput(val) {
      // 150ms debounce to avoid re-rendering the list on every keystroke.
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => {
        this.search = val || '';
      }, 150);
    },
    value(val) {
      if (val) {
        // Focus search input shortly after menu mounts.
        this.$nextTick(() => {
          const field = this.$refs.searchField;
          if (field && typeof field.focus === 'function') {
            field.focus();
          }
        });
      } else {
        // Reset search when menu closes so the next open starts clean.
        this.searchInput = '';
        this.search = '';
      }
    },
  },
  beforeDestroy() {
    clearTimeout(this.searchTimer);
  },
  methods: {
    onMenuInput(val) {
      this.$emit('input', val);
      if (val) this.$emit('open');
    },
    /**
     * Returns the href for a facility list item, or undefined for the current
     * one (no-op click). Real anchors give us free native context-menu support:
     * right-click → "Open in New Tab", Ctrl/Cmd-click → new tab, middle-click
     * → new tab. All without custom UI.
     */
    hrefFor(facility) {
      if (!facility || !facility.id) return undefined;
      if (facility.id === this.currentFacilityId) return undefined;
      return '/' + facility.id;
    },
    /**
     * Left-click handler. For modifier-clicks (Ctrl/Cmd/Shift) we let the
     * browser's native <a> behavior take over so "open in new tab" works.
     * For plain clicks we force a full page reload via location.assign to
     * guarantee fresh data (avoids Vue Router's SPA-only in-place swap).
     */
    async handleItemClick(facility, event) {
      if (!facility || !facility.id) return;

      // Clicking the current facility is a no-op — just close the menu.
      if (facility.id === this.currentFacilityId) {
        if (event) event.preventDefault();
        this.$emit('input', false);
        return;
      }

      // Modifier/middle clicks → let native <a> open in new tab/window. These keep the
      // current tab (and any edit session) intact, so no exit handshake is needed.
      if (event && (event.ctrlKey || event.metaKey || event.shiftKey || event.button === 1)) {
        this.$emit('input', false);
        return;
      }

      // Plain left-click → force full page reload for fresh data.
      if (event) event.preventDefault();
      this.$emit('input', false);

      // builder-v1: a full reload destroys an active dashboard edit session, so await its
      // exit coordinator first (drain/Retry/Discard). Flag off or no active session resolves
      // immediately → unchanged behavior. `false` = the user kept editing; cancel the switch.
      if (!(await guardDashboardExit())) return;
      window.location.assign('/' + facility.id);
    },
    /**
     * Middle-click (auxclick with button=1) needs explicit handling in some
     * browsers; the native <a> already opens it in a new tab, but we still
     * want the menu to close.
     */
    handleAuxClick(facility, event) {
      if (event && event.button === 1) {
        this.$emit('input', false);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.facility-activator {
  background-color: transparent !important;
  color: var(--v-bg_force_white-base) !important;
  font-size: 13px;
  font-weight: 500;
  letter-spacing: 0.01em;
  text-transform: none;
  min-width: 0 !important;
  padding: 0 !important;

  &:hover::before,
  &:focus::before,
  &:active::before {
    background-color: rgba(255, 255, 255, 0.06);
  }
}

.activator-lead-icon {
  margin-right: 12px !important;
}

.activator-label {
  max-width: 150px;
  display: inline-block;
  vertical-align: middle;
}

@media (max-width: 600px) {
  .activator-label {
    max-width: 100px;
  }
}

.facility-dropdown-card {
  min-width: 300px;
}

.compact-list {
  padding-top: 0 !important;
  padding-bottom: 0 !important;
}

.compact-subheader {
  height: 28px !important;
  min-height: 28px !important;
  font-size: 10px !important;
  font-weight: 600 !important;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  padding: 0 12px !important;
}

.compact-list-item {
  min-height: 36px !important;
  padding: 0 8px !important;
  transition: background-color 0.15s ease;
}

.compact-list-item .v-list-item__avatar {
  margin-top: 0 !important;
  margin-bottom: 0 !important;
}

.compact-chip {
  height: 16px !important;
  font-size: 9px !important;
  font-weight: 600 !important;
  letter-spacing: 0.04em;
  padding: 0 6px !important;
}

.favorites-section-header {
  color: var(--v-primary-base);
}

.all-section-header {
  color: var(--v-text_secondary-base);
}

.facility-separator {
  margin-left: 16px;
  margin-right: 16px;
  border-color: rgba(0, 0, 0, 0.08);
}

.facility-item--current {
  background: linear-gradient(
    90deg,
    rgba(var(--v-success-base-rgb), 0.08),
    transparent
  );
  border-left: 3px solid var(--v-success-base);
  margin: 2px 4px;
  border-radius: 0 8px 8px 0;
}

.compact-list-item .v-list-item__title {
  font-size: 13px !important;
  font-weight: 500 !important;
  letter-spacing: 0.01em !important;
  color: var(--v-text-base) !important;
}

.v-text-field input {
  font-size: 13px !important;
  letter-spacing: 0.01em !important;
}

.v-text-field input::placeholder {
  font-size: 13px !important;
  letter-spacing: 0.01em !important;
  opacity: 0.6;
}
</style>
